import React from 'react';
import { Container, Section } from '@/components/layout/Container';

const LAST_UPDATED = 'February 13, 2026';

export const TermsPage: React.FC = () => {
  return (
    <main>
      <Section>
        <Container size="md">
          <h1 className="text-h1 font-bold mb-4">Terms of Use</h1>
          <p className="text-body text-text-secondary mb-6">
            These Terms govern your use of this website and your interaction with advisory services
            offered through it.
          </p>
          <p className="text-body-s text-text-tertiary mb-8">Last updated: {LAST_UPDATED}</p>

          <div className="space-y-8 text-body text-text-secondary">
            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">Acceptance of terms</h2>
              <p>
                By accessing or using this website, you agree to these Terms and to our Privacy Policy.
                If you do not agree, do not use this site.
              </p>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">Service scope</h2>
              <p>
                This site presents professional advisory services for healthcare strategy, clinical
                operations, and health technology integration. Information is general and business-oriented.
              </p>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">No medical care</h2>
              <p>
                Nothing on this site is personal medical advice, diagnosis, or treatment. Use of this
                site, forms, or communications does not create a doctor-patient relationship.
              </p>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">Permitted use</h2>
              <div className="space-y-3">
                <p>You agree to use the site lawfully and not to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>attempt unauthorized access to systems, APIs, or data,</li>
                  <li>interfere with site operations, security, or availability,</li>
                  <li>submit false, harmful, or abusive content through forms or communications,</li>
                  <li>use site content in a misleading, unlawful, or infringing way.</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">Inquiries and engagement</h2>
              <p>
                Contact submissions are requests for discussion only and do not create a binding
                service agreement. Any advisory engagement, scope, timeline, fee, deliverables, and
                confidentiality obligations are defined in a separate written agreement.
              </p>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">Fees and payments</h2>
              <p>
                Fees, billing terms, taxes, and payment schedules are set out in signed proposals or
                agreements. Unless explicitly agreed, no obligations to provide services arise from
                website correspondence alone.
              </p>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">Intellectual property</h2>
              <p>
                All website content, branding, copy, graphics, and materials are protected by
                intellectual property laws and remain the property of their respective owners. No
                license is granted except limited personal viewing for legitimate business evaluation.
              </p>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">Confidentiality</h2>
              <p>
                We treat professional communications with care. Where needed, formal confidentiality
                commitments are handled through NDAs or service agreements.
              </p>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">Third-party services and links</h2>
              <p>
                This site may reference third-party services or links. We are not responsible for the
                content, policies, or practices of third-party websites and tools.
              </p>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">Analytics and SEO measurement</h2>
              <div className="space-y-3">
                <p>
                  We may use analytics and measurement tooling to monitor usage across pages and
                  applications, including click behavior, engagement flow, and performance metrics.
                </p>
                <p>
                  We may also integrate search-performance data (for example Search Console metrics)
                  for SEO reporting and dashboarding. Use of the site constitutes acceptance of this
                  measurement, subject to applicable law and consent requirements.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">No misuse of tracking systems</h2>
              <p>
                You must not attempt to manipulate analytics events, interfere with telemetry
                collection, or intentionally generate fraudulent traffic, clicks, or conversion data.
              </p>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">Disclaimer of warranties</h2>
              <p>
                The website is provided on an “as is” and “as available” basis. To the extent
                permitted by law, we disclaim warranties of uninterrupted availability, accuracy,
                merchantability, and fitness for a particular purpose.
              </p>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">Limitation of liability</h2>
              <p>
                To the maximum extent permitted by law, we are not liable for indirect, incidental,
                special, consequential, or punitive damages arising from website use. Total liability
                for website-related claims is limited to amounts paid directly for website access, if any.
              </p>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">Indemnity</h2>
              <p>
                You agree to indemnify and hold harmless the site owner from claims, liabilities, and
                costs resulting from your unlawful use of the site or breach of these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">Governing law and venue</h2>
              <p>
                These Terms are governed by Norwegian law. Disputes shall be resolved in the courts of
                Norway unless mandatory law requires otherwise.
              </p>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">Changes to terms</h2>
              <p>
                We may update these Terms from time to time. Continued use of the site after changes
                are posted means you accept the revised Terms.
              </p>
            </section>

            <section>
              <h2 className="text-h4 font-semibold text-text-primary mb-2">Contact</h2>
              <p>
                Questions about these Terms can be sent to{' '}
                <a href="mailto:christer@invested.no" className="text-apple-blue hover:underline">
                  christer@invested.no
                </a>
                .
              </p>
            </section>
          </div>
        </Container>
      </Section>
    </main>
  );
};

export default TermsPage;
